import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Button } from '@/components/ui/Button';
import { Screen } from '@/components/ui/Screen';
import { TextField } from '@/components/ui/TextField';
import { useThemePalette } from '@/config/theme';
import { useFavorites } from '@/features/favorites/hooks';
import { useAllTeams, useCompetitions, useFollowablePlayers } from '@/features/leagues/hooks';
import { useProfile, useUpdateProfile } from '@/features/profile/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { addFavorite, type PlayerWithTeam } from '@/lib/data/provider';
import {
  clearPendingOnboarding,
  loadPendingOnboarding,
  savePendingOnboarding,
} from '@/lib/onboarding';
import type { FavoriteEntityType, Tables } from '@/lib/supabase/types';
import { sortByPopularity } from '@/lib/utils/popularTeams';

const isWorldCup = (name: string) => /world cup/i.test(name);

// Follow your teams (nations first) / competitions / players. A signed-in user
// confirms their display name as a final step; the BEFORE-sign-in picker is a
// SINGLE team-pick screen (fastest path to activation) and hands off to auth -
// leagues + players are refined later in-app. The signed-in refinement keeps the
// full set so a returning user can still curate competitions and players here.
type Step = 'teams' | 'leagues' | 'players' | 'name';
const PRE_AUTH_STEPS: Step[] = ['teams'];
const SIGNED_IN_STEPS: Step[] = ['teams', 'leagues', 'players', 'name'];

function ProgressDots({ steps, index }: { steps: Step[]; index: number }) {
  return (
    <View className="flex-row gap-1.5">
      {steps.map((step, i) => (
        <View
          key={step}
          className={`h-1.5 rounded-full ${i <= index ? 'bg-volt' : 'bg-line'}`}
          style={{ width: i === index ? 26 : 14 }}
        />
      ))}
    </View>
  );
}

function TeamCard({
  team,
  selected,
  onToggle,
}: {
  team: Tables<'teams'>;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onPress={onToggle}
      style={{ width: '31%' }}
      className={`mb-3 items-center gap-2 rounded-2xl border px-2 py-3 ${
        selected ? 'border-volt bg-volt/10' : 'border-line bg-surface active:bg-surface-2'
      }`}
    >
      <TeamCrest uri={team.logo_url} name={team.name} size="lg" />
      <AppText variant="micro" weight="semibold" className="text-center text-ink" numberOfLines={1}>
        {team.short_name ?? team.name}
      </AppText>
    </Pressable>
  );
}

function PlayerCard({
  player,
  selected,
  onToggle,
}: {
  player: PlayerWithTeam;
  selected: boolean;
  onToggle: () => void;
}) {
  const name = player.known_as ?? player.name;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onPress={onToggle}
      style={{ width: '31%' }}
      className={`mb-3 items-center gap-2 rounded-2xl border px-2 py-3 ${
        selected ? 'border-volt bg-volt/10' : 'border-line bg-surface active:bg-surface-2'
      }`}
    >
      <View>
        <PlayerAvatar uri={player.photo_url} name={name} size={52} />
        {player.team?.logo_url ? (
          <View className="absolute -end-1.5 -bottom-1.5 h-7 w-7 items-center justify-center overflow-hidden rounded-full border-2 border-surface bg-surface">
            <TeamCrest uri={player.team.logo_url} name={player.team.name} size="sm" />
          </View>
        ) : null}
      </View>
      <AppText variant="micro" weight="semibold" className="text-center text-ink" numberOfLines={1}>
        {name}
      </AppText>
    </Pressable>
  );
}

function CompetitionRow({
  competition,
  selected,
  active,
  onToggle,
}: {
  competition: Tables<'competitions'>;
  selected: boolean;
  /** The currently-running competition (World Cup): a gold star + glow frame. */
  active?: boolean;
  onToggle: () => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onPress={onToggle}
      className={`mb-2.5 flex-row items-center gap-3 rounded-2xl border px-3.5 py-3 ${
        active || selected ? 'border-volt bg-volt/10' : 'border-line bg-surface active:bg-surface-2'
      }`}
      style={
        active
          ? { borderWidth: 1.5, shadowColor: palette.volt, shadowOpacity: 0.5, shadowRadius: 11, shadowOffset: { width: 0, height: 0 } }
          : undefined
      }
    >
      <TeamCrest uri={competition.logo_url} name={competition.name} size="md" />
      <View className="flex-1">
        <View className="flex-row items-center gap-1.5">
          <AppText variant="caption" weight="semibold" className="shrink text-ink" numberOfLines={1}>
            {competition.name}
          </AppText>
          {active ? <Ionicons name="star" size={12} color={palette.captain} /> : null}
        </View>
        {active ? (
          <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-volt-bright" numberOfLines={1}>
            {t('common.live')}
          </AppText>
        ) : competition.country ? (
          <AppText variant="micro" numberOfLines={1}>
            {competition.country}
          </AppText>
        ) : null}
      </View>
      <View
        className={`h-6 w-6 items-center justify-center rounded-full ${
          selected ? 'bg-volt' : 'border border-line'
        }`}
      >
        {selected ? (
          <AppText variant="micro" weight="bold" className="text-on-volt">
            ✓
          </AppText>
        ) : null}
      </View>
    </Pressable>
  );
}

/**
 * Guided setup picker. Runs in two modes:
 *  - BEFORE sign-in (no session): the guest follows teams / leagues / players;
 *    the choices are buffered and the screen hands off to the auth screen.
 *  - AFTER sign-in: the buffered choices are applied to user_favorites and the
 *    user confirms a display name, which flips display_name_confirmed and lets
 *    the tabs gate through. A signed-in user with no buffer can pick here too.
 */
export default function WelcomeScreen() {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { userId } = useAuth();
  const profileQuery = useProfile();
  const updateProfile = useUpdateProfile();
  const favorites = useFavorites();
  const teamsQuery = useAllTeams();
  const competitionsQuery = useCompetitions();
  const playersQuery = useFollowablePlayers();

  const steps = userId ? SIGNED_IN_STEPS : PRE_AUTH_STEPS;
  const [step, setStep] = useState<Step>('teams');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [teamQuery, setTeamQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [finalizing, setFinalizing] = useState(false);
  const prefilled = useRef(false);
  const seeded = useRef(false);
  const finalized = useRef(false);

  const composeKey = (type: FavoriteEntityType, id: string) => `${type}:${id}`;
  const parseSelected = (): { type: FavoriteEntityType; id: string }[] =>
    [...selected].map((entry) => {
      const [type, id] = entry.split(':');
      return { type: type as FavoriteEntityType, id };
    });

  const applyFollows = async (uid: string) => {
    for (const fav of parseSelected()) await addFavorite(uid, fav.type, fav.id);
  };

  // Once signed in, apply a pre-auth picker's buffered follows and pass through.
  const alreadyConfirmed = profileQuery.data?.display_name_confirmed === true;
  useEffect(() => {
    if (!userId || finalized.current || alreadyConfirmed) return;
    let active = true;
    (async () => {
      const pending = await loadPendingOnboarding();
      if (!pending || pending.favorites.length === 0 || !active) return;
      finalized.current = true;
      setFinalizing(true);
      try {
        for (const fav of pending.favorites) await addFavorite(userId, fav.type, fav.id);
        await clearPendingOnboarding();
        updateProfile.mutate(
          { display_name_confirmed: true },
          // Land on the daily hub (lists today's matches to predict) rather than a
          // static profile, so the very next tap can be the first prediction.
          { onSuccess: () => router.replace('/today' as never) },
        );
      } catch {
        if (active) {
          finalized.current = false;
          setFinalizing(false); // fall back to the manual picker
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [userId, router, updateProfile, alreadyConfirmed]);

  // Signed-in re-onboarding: seed the picker from existing follows + the name.
  useEffect(() => {
    if (!seeded.current && userId && favorites.data) {
      setSelected(new Set(favorites.data.map((f) => `${f.entity_type}:${f.entity_id}`)));
      seeded.current = true;
    }
  }, [userId, favorites.data]);

  useEffect(() => {
    if (!prefilled.current && profileQuery.data?.display_name) {
      setName(profileQuery.data.display_name);
      prefilled.current = true;
    }
  }, [profileQuery.data?.display_name]);

  const isSelected = (type: FavoriteEntityType, id: string) => selected.has(composeKey(type, id));
  const toggle = (type: FavoriteEntityType, id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const key = composeKey(type, id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Pre-auth: buffer picks as they're made so an app kill mid-onboarding doesn't
  // lose them (the final hand-off saves too; this just survives an early exit).
  // Guarded on a non-empty set so the empty initial render can't clobber a buffer
  // a returning user already has. Signed-in picks write straight to favorites.
  useEffect(() => {
    if (userId || selected.size === 0) return;
    void savePendingOnboarding({ favorites: parseSelected() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, userId]);

  // The currently-running World Cup, so we can float its nations + star its row.
  const wcId = useMemo(
    () => (competitionsQuery.data ?? []).find((c) => isWorldCup(c.name))?.id,
    [competitionsQuery.data],
  );

  // World Cup first (it is live), then the rest in their existing order.
  const competitions = useMemo(() => {
    const all = competitionsQuery.data ?? [];
    return [...all].sort((a, b) => Number(isWorldCup(b.name)) - Number(isWorldCup(a.name)));
  }, [competitionsQuery.data]);

  // Pre-auth runs as a single, focused screen: a curated grid of the most popular
  // teams with NO search box, so a guest can pick fast and hand off to sign-in.
  // The signed-in refinement keeps the full searchable list.
  const isPreAuth = !userId;
  const teams = useMemo(() => {
    const all = teamsQuery.data ?? [];
    const q = isPreAuth ? '' : teamQuery.trim().toLowerCase();
    const filtered = q
      ? all.filter((team) => `${team.name} ${team.short_name ?? ''}`.toLowerCase().includes(q))
      : all;
    // World Cup season: national teams float to the very top, then the big clubs.
    const nationals = sortByPopularity(filtered.filter((team) => wcId != null && team.competition_id === wcId));
    const clubs = sortByPopularity(filtered.filter((team) => wcId == null || team.competition_id !== wcId));
    // Pre-auth: a tight, curated 12. Signed-in: the broader searchable set.
    const limit = isPreAuth ? 12 : q ? 80 : 48;
    return [...nationals, ...clubs].slice(0, limit);
  }, [teamsQuery.data, teamQuery, wcId, isPreAuth]);

  const avatar = profileQuery.data?.avatar_url;
  const stepIndex = steps.indexOf(step);
  const isLastStep = stepIndex === steps.length - 1;

  // Pre-auth: buffer the follows and hand off to sign-in. Signed-in: write the
  // follows now and confirm the profile (name step gates this branch).
  const finishPreAuth = async () => {
    await savePendingOnboarding({ favorites: parseSelected() });
    router.replace('/auth/sign-in');
  };

  const finishSignedIn = () => {
    if (!userId) return;
    if (name.trim().length < 2) {
      setError(t('auth.errors.shortName'));
      return;
    }
    setError(null);
    setFinalizing(true);
    applyFollows(userId)
      .then(() => clearPendingOnboarding())
      .then(() =>
        updateProfile.mutate(
          { display_name: name.trim(), display_name_confirmed: true },
          // Land on the daily hub (today's matches to predict), not the profile, so
          // activation continues straight into a first prediction.
          { onSuccess: () => router.replace('/today' as never) },
        ),
      )
      .catch(() => {
        setFinalizing(false);
        setError(t('auth.errors.generic'));
      });
  };

  const goNext = () => {
    if (step === 'name') {
      finishSignedIn();
      return;
    }
    const next = steps[stepIndex + 1];
    if (next) {
      setStep(next);
      return;
    }
    // Last step of the pre-auth picker (no name step) -> hand off to sign-in.
    void finishPreAuth();
  };

  const goBack = () => {
    const prev = steps[stepIndex - 1];
    if (prev) setStep(prev);
  };

  if (finalizing) {
    return (
      <Screen watermark={false}>
        <View className="flex-1 items-center justify-center gap-4 px-8">
          <ActivityIndicator color={palette.volt} />
          <AppText variant="caption" className="text-center text-soft">
            {t('auth.settingUp')}
          </AppText>
        </View>
      </Screen>
    );
  }

  const buttonLabel = step === 'name'
    ? t('auth.finish')
    : isLastStep
      ? t('auth.continue')
      : t('auth.next');

  return (
    <Screen watermark={false}>
      <View className="flex-1">
        <View className="items-center px-6 pb-2 pt-4">
          <ProgressDots steps={steps} index={stepIndex} />
        </View>

        {step === 'name' ? (
          <View className="flex-1 justify-center gap-7 px-6">
            <View className="items-center gap-4">
              {avatar ? (
                <Image
                  source={{ uri: avatar }}
                  style={{ height: 88, width: 88, borderRadius: 44, borderWidth: 2, borderColor: palette.volt }}
                  contentFit="cover"
                  cachePolicy="memory-disk"
                />
              ) : (
                <View
                  style={{ height: 88, width: 88 }}
                  className="items-center justify-center rounded-full border-2 border-volt bg-inset"
                >
                  <AppText variant="title" weight="bold" className="text-volt-bright">
                    {(name || '?').charAt(0).toUpperCase()}
                  </AppText>
                </View>
              )}
              <View className="items-center gap-1.5">
                <AppText variant="title" className="text-center">
                  {t('auth.onboardingTitle')}
                </AppText>
                <AppText variant="caption" className="max-w-[300px] text-center leading-5">
                  {t('auth.onboardingBody')}
                </AppText>
              </View>
            </View>
            <View className="gap-3">
              <TextField
                label={t('auth.displayName')}
                value={name}
                onChangeText={setName}
                autoComplete="name"
                maxLength={40}
              />
              {error ? (
                <AppText variant="caption" className="text-loss">
                  {error}
                </AppText>
              ) : null}
            </View>
          </View>
        ) : step === 'teams' ? (
          <View className="flex-1 gap-3 px-5">
            <View className="gap-1">
              <AppText variant="title">{t('auth.onboardingTeamsTitle')}</AppText>
              {isPreAuth ? (
                <AppText variant="caption" className="text-soft">
                  {t('auth.onboardingTeamsBody')}
                </AppText>
              ) : null}
            </View>
            {/* Signed-in refinement keeps the full searchable list; the pre-auth
                first-run path is a tight curated grid (no search) for speed. */}
            {isPreAuth ? null : (
              <TextField
                placeholder={t('auth.searchTeams')}
                value={teamQuery}
                onChangeText={setTeamQuery}
                autoCapitalize="none"
              />
            )}
            {isPreAuth && teamsQuery.isPending ? (
              <View className="flex-1 items-center justify-center">
                <ActivityIndicator color={palette.volt} />
              </View>
            ) : (
              <ScrollView contentContainerStyle={{ paddingBottom: 12 }} keyboardShouldPersistTaps="handled">
                <View className="flex-row flex-wrap justify-between">
                  {teams.map((team) => (
                    <TeamCard
                      key={team.id}
                      team={team}
                      selected={isSelected('team', team.id)}
                      onToggle={() => toggle('team', team.id)}
                    />
                  ))}
                </View>
              </ScrollView>
            )}
          </View>
        ) : step === 'leagues' ? (
          <View className="flex-1 gap-3 px-5">
            <AppText variant="title">{t('auth.onboardingLeaguesTitle')}</AppText>
            <AppText variant="caption" className="text-soft">
              {t('auth.onboardingLeaguesBody')}
            </AppText>
            <ScrollView contentContainerStyle={{ paddingBottom: 12 }}>
              {competitions.map((competition) => (
                <CompetitionRow
                  key={competition.id}
                  competition={competition}
                  selected={isSelected('competition', competition.id)}
                  active={isWorldCup(competition.name)}
                  onToggle={() => toggle('competition', competition.id)}
                />
              ))}
            </ScrollView>
          </View>
        ) : (
          <View className="flex-1 gap-3 px-5">
            <AppText variant="title">{t('auth.onboardingPlayersTitle')}</AppText>
            <AppText variant="caption" className="text-soft">
              {t('auth.onboardingPlayersBody')}
            </AppText>
            <ScrollView contentContainerStyle={{ paddingBottom: 12 }} keyboardShouldPersistTaps="handled">
              <View className="flex-row flex-wrap justify-between">
                {(playersQuery.data ?? []).map((player) => (
                  <PlayerCard
                    key={player.id}
                    player={player}
                    selected={isSelected('player', player.id)}
                    onToggle={() => toggle('player', player.id)}
                  />
                ))}
              </View>
            </ScrollView>
          </View>
        )}

        <View className="flex-row items-center gap-3 px-5 pb-4 pt-2">
          {stepIndex > 0 ? (
            <Button label={t('common.back')} variant="ghost" compact onPress={goBack} />
          ) : null}
          <View className="flex-1" />
          <Button
            label={buttonLabel}
            loading={step === 'name' && updateProfile.isPending}
            onPress={goNext}
          />
        </View>
      </View>
    </Screen>
  );
}
